import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useOrgPreset } from '@/hooks/shared/useOrgPreset';
import { usePermissions } from '@/hooks/permissions/usePermissions';
import { isRouteAllowedByPreset, rotaDesativadaPorPreset } from '@/lib/orgPresets';
import { routeToPermission } from '@/lib/permissions/catalog';
import { PastaPagaGuard } from '@/features/atticus-rateio/escritorio/PastaPagaGuard';

/**
 * Fecha o ambiente de uma org com preset de interface: rota fora da lista do preset
 * redireciona para a home dele. Esconder só o item do menu deixaria a URL aberta.
 *
 * Preset só de marca com `ajustes` (ex.: org Atticus): a página fora de linha redireciona para
 * o destino da regra. Se a pessoa não tem a chave do destino, vai para "/", que escolhe a
 * entrada pelas permissões dela; assim o redirect nunca termina em "Acesso negado".
 *
 * Sem preset, ou com preset só de marca sem ajustes, é um repasse puro. O console
 * Super-Admin e a Supervisão vivem fora do AppLayout e não passam por aqui.
 */
export const OrgPresetRouteGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const preset = useOrgPreset();
  const { pathname } = useLocation();
  const { has, loading: permsLoading } = usePermissions();

  if (preset?.shell && !isRouteAllowedByPreset(preset, pathname)) {
    return <Navigate to={preset.shell.homeRoute} replace />;
  }

  const destino = rotaDesativadaPorPreset(preset, pathname);
  if (destino) {
    if (permsLoading) return null;
    return <Navigate to={has(routeToPermission(destino)) ? destino : '/'} replace />;
  }
  // Escritório do rateio: a página do negócio só abre para pasta paga.
  const negocio = preset?.id === 'atticus_escritorio' ? /^\/deals\/([^/]+)/.exec(pathname) : null;
  if (negocio) {
    return <PastaPagaGuard dealId={negocio[1]}>{children}</PastaPagaGuard>;
  }
  return <>{children}</>;
};
